(() => {
  const init = (root) => {
    if (!root || root.dataset.vigoPdp27Ready === 'true') return;
    root.dataset.vigoPdp27Ready = 'true';

    const panes = [...root.querySelectorAll('[data-vigo-media-pane]')];
    const thumbs = [...root.querySelectorAll('[data-vigo-media-thumb]')];
    let current = Math.max(0, panes.findIndex((pane) => pane.classList.contains('is-active')));

    const zoomTrigger = root.querySelector('[data-vigo-media-zoom]');
    const zoomDialog = root.querySelector('[data-vigo-zoom-dialog]');
    const zoomImage = root.querySelector('[data-vigo-zoom-image]');

    const activeImage = () => panes[current]?.querySelector('img') || null;

    const syncZoomState = () => {
      if (!zoomTrigger) return;
      zoomTrigger.hidden = !activeImage();
    };

    const showMedia = (index) => {
      if (!panes.length) return;
      current = (index + panes.length) % panes.length;
      panes.forEach((pane, i) => {
        const active = i === current;
        pane.classList.toggle('is-active', active);
        pane.hidden = !active;
      });
      thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === current));
      syncZoomState();
    };

    const openZoom = () => {
      const image = activeImage();
      if (!image || !zoomDialog || !zoomImage) return;
      zoomImage.src = image.currentSrc || image.src;
      zoomImage.alt = image.alt || '';
      if (typeof zoomDialog.showModal === 'function') zoomDialog.showModal();
      else zoomDialog.setAttribute('open', '');
      document.documentElement.classList.add('vigo-zoom-open');
    };

    const closeZoom = () => {
      if (!zoomDialog) return;
      if (typeof zoomDialog.close === 'function' && zoomDialog.open) zoomDialog.close();
      else zoomDialog.removeAttribute('open');
      document.documentElement.classList.remove('vigo-zoom-open');
    };

    root.querySelector('[data-vigo-media-prev]')?.addEventListener('click', () => showMedia(current - 1));
    root.querySelector('[data-vigo-media-next]')?.addEventListener('click', () => showMedia(current + 1));
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => showMedia(Number(thumb.dataset.vigoMediaThumb || 0))));

    zoomTrigger?.addEventListener('click', openZoom);
    panes.forEach((pane) => pane.querySelector('img')?.addEventListener('click', openZoom));
    root.querySelector('[data-vigo-zoom-close]')?.addEventListener('click', closeZoom);
    zoomDialog?.addEventListener('click', (event) => {
      if (event.target === zoomDialog) closeZoom();
    });
    zoomDialog?.addEventListener('close', () => document.documentElement.classList.remove('vigo-zoom-open'));
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && zoomDialog?.open) closeZoom();
    });
    syncZoomState();

    const deliveryDate = root.querySelector('[data-vigo-delivery-date]');
    if (deliveryDate) {
      const eta = new Date();
      eta.setDate(eta.getDate() + 5);
      deliveryDate.dateTime = eta.toISOString().slice(0, 10);
      deliveryDate.textContent = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' }).format(eta);
    }

    const qtyInput = root.querySelector('[data-vigo-qty-input]');
    root.querySelector('[data-vigo-qty-minus]')?.addEventListener('click', () => {
      if (!qtyInput) return;
      qtyInput.value = String(Math.max(1, Number(qtyInput.value || 1) - 1));
    });
    root.querySelector('[data-vigo-qty-plus]')?.addEventListener('click', () => {
      if (!qtyInput) return;
      qtyInput.value = String(Math.max(1, Number(qtyInput.value || 1) + 1));
    });

    const variantsNode = root.querySelector('[data-vigo-variants-json]');
    const configNode = root.querySelector('[data-vigo-pdp-config]');
    let variants = [];
    let config = { currency: 'GBP', locale: 'en-GB', fencing: false };
    try { variants = JSON.parse(variantsNode?.textContent || '[]'); } catch (_) {}
    try { config = { ...config, ...JSON.parse(configNode?.textContent || '{}') }; } catch (_) {}

    const formatter = new Intl.NumberFormat(config.locale || 'en-GB', {
      style: 'currency',
      currency: config.currency || 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const money = (cents) => formatter.format((Number(cents) || 0) / 100);

    const price = root.querySelector('[data-vigo-price]');
    const compare = root.querySelector('[data-vigo-compare]');
    const sku = root.querySelector('[data-vigo-sku]');
    const stock = root.querySelector('[data-vigo-stock]');
    const stockText = root.querySelector('[data-vigo-stock-text]');
    const atc = root.querySelector('[data-vigo-atc]');
    const atcText = root.querySelector('[data-vigo-atc-text]');
    const sellingUnit = root.querySelector('[data-vigo-selling-unit]');
    const variantInput = root.querySelector('[data-vigo-variant-input]');

    const unitForVariant = (variant) => {
      if (!config.fencing) return sellingUnit?.textContent?.trim() || 'PER UNIT';
      const title = String(variant?.title || '').toLowerCase();
      const isSet = title.includes('set of') || title.includes('fence set') || title.includes('fencing set') || Number(variant?.price) === 10000 || Number(variant?.price) === 17500;
      return isSet ? 'PER SET' : 'PER BOARD';
    };

    const updateVariant = (variantId) => {
      const variant = variants.find((item) => String(item.id) === String(variantId));
      if (!variant) return;
      if (variantInput) variantInput.value = String(variant.id);
      if (price) price.textContent = money(variant.price);
      if (compare) {
        const onSale = Number(variant.compare_at_price) > Number(variant.price);
        compare.textContent = onSale ? money(variant.compare_at_price) : '';
        compare.classList.toggle('is-hidden', !onSale);
      }
      if (sku) sku.textContent = variant.sku || '—';
      if (stock) stock.classList.toggle('is-out', !variant.available);
      if (stockText) stockText.textContent = variant.available ? 'In stock' : 'Sold out';
      if (atc) atc.disabled = !variant.available;
      if (atcText) atcText.textContent = variant.available ? 'Add to cart' : 'Sold out';
      if (sellingUnit) sellingUnit.textContent = unitForVariant(variant);

      if (variant.featured_media?.id) {
        const paneIndex = panes.findIndex((pane) => String(pane.dataset.mediaId) === String(variant.featured_media.id));
        if (paneIndex >= 0) showMedia(paneIndex);
      }
    };

    const select = root.querySelector('[data-vigo-variant-select]');
    select?.addEventListener('change', (event) => updateVariant(event.target.value));
  };

  const initAll = () => document.querySelectorAll('[data-vigo-pdp27]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
